'use client';

import type {
  MysteryRoomDoorRuntimeState,
  MysteryRoomFacing,
  MysteryRoomHeroState,
  MysteryRoomLocalRuntime,
  MysteryRoomNpcState,
} from '../core/localRuntime';
import { normalizeInputVector } from './inputSystem';
import { buildActorRouteToPoint, resolveImmediateRouteWaypoint } from './navigationSystem';
import { isMysteryRoomCustomObjectAlphaOpaqueAtPoint } from '../world/assetAlphaCollision';
import { rectContainsPoint } from '../world/mapTypes';
import type {
  MysteryRoomCollisionShape,
  MysteryRoomCustomObjectDefinition,
  MysteryRoomMapDefinition,
  MysteryRoomRect,
  MysteryRoomVec2,
} from '../world/mapTypes';

type MovableActor = Pick<MysteryRoomHeroState, 'position' | 'radius'> | Pick<MysteryRoomNpcState, 'position' | 'radius'>;
type TraverseOptions = {
  ignoreCustomObjectId?: string;
};
type StepActorOptions = {
  ignoreCustomObjectId?: string;
  pushStrengthKg?: number;
};
type StepActorResult = {
  map: MysteryRoomMapDefinition;
  position: MysteryRoomVec2;
};
type CustomObjectBlocker = {
  object: MysteryRoomCustomObjectDefinition;
  index: number;
};

const NAVIGATION_ARRIVAL_MARGIN = 8;
const DEFAULT_HERO_STRENGTH_KG = 50;
const DEFAULT_PUSHABLE_OBJECT_WEIGHT_KG = 25;
const HERO_CARRY_REACH = 90;
const HERO_CARRY_GAP = 8;
const HERO_CARRY_MIN_SPEED_MULTIPLIER = 0.35;
const HERO_CARRY_MAX_SLOWDOWN = 0.55;

export function canTraversePoint(
  map: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
  doors: MysteryRoomDoorRuntimeState[],
  radius = 0,
  options: TraverseOptions = {},
) {
  const samples = actorTraversalSamples(point, radius);

  return samples.every(
    (sample) => pointInsideWalkableArea(map, sample) && !pointHitsMovementBlocker(map, sample, doors, options),
  );
}

export function resolveZoneId(map: MysteryRoomMapDefinition, point: MysteryRoomVec2) {
  return map.rooms.find((room) => room.floor.some((floor) => rectContainsPoint(floor, point)))?.id ?? map.spawnZoneId;
}

export function facingFromVelocity(velocity: MysteryRoomVec2, fallback: MysteryRoomFacing): MysteryRoomFacing {
  if (Math.abs(velocity.x) < 0.0001 && Math.abs(velocity.y) < 0.0001) {
    return fallback;
  }

  if (Math.abs(velocity.x) >= Math.abs(velocity.y)) {
    return velocity.x >= 0 ? 'right' : 'left';
  }

  return velocity.y >= 0 ? 'down' : 'up';
}

export function updateHeroMovement(
  map: MysteryRoomMapDefinition,
  hero: MysteryRoomHeroState,
  input: { move: MysteryRoomVec2; pointerTarget: MysteryRoomVec2 | null },
  doors: MysteryRoomDoorRuntimeState[],
  dtMs: number,
  inventoryItemIds: string[] = [],
) {
  const manual = normalizeInputVector(input.move);
  const dtSeconds = dtMs / 1000;
  const hasManualInput = manual.x !== 0 || manual.y !== 0;
  const pushStrengthKg = hero.strengthKg ?? DEFAULT_HERO_STRENGTH_KG;
  const carriedObject = getCarriedObject(map, hero.carriedObjectId);
  const carriedWeightKg = carriedObject ? customObjectWeightKg(carriedObject) : 0;
  const effectiveSpeed = hero.speed * carriedObjectSpeedMultiplier(carriedWeightKg, pushStrengthKg);
  const movementOptions: StepActorOptions = {
    ignoreCustomObjectId: carriedObject?.id,
    pushStrengthKg: carriedObject ? undefined : pushStrengthKg,
  };

  if (hasManualInput) {
    const velocity = { x: manual.x * effectiveSpeed, y: manual.y * effectiveSpeed };
    const movement = stepActorInternal(map, hero, velocity, dtSeconds, doors, movementOptions);
    const nextPosition = movement.position;
    const nextHero = {
      ...hero,
      position: nextPosition,
      velocity,
      facing: facingFromVelocity(velocity, hero.facing),
      zoneId: resolveZoneId(movement.map, nextPosition),
      movementMode: 'manual' as const,
      animationState: 'walk' as const,
      carriedObjectWeightKg: carriedWeightKg,
    };
    const carryResult = syncCarriedObjectAfterHeroMove(movement.map, hero, nextHero, doors);

    return {
      map: carryResult.map,
      hero: carryResult.hero,
      pointerTarget: null,
    };
  }

  if (input.pointerTarget) {
    const route = buildActorRouteToPoint(
      map,
      doors,
      {
        accessLevels: hero.accessLevels,
        actorType: 'hero',
        inventoryItemIds,
        position: hero.position,
        zoneId: hero.zoneId,
      },
      input.pointerTarget,
      hero.radius,
    );
    if (!route.reachable) {
      return {
        map,
        hero: {
          ...hero,
          velocity: { x: 0, y: 0 },
          movementMode: 'idle' as const,
          animationState: 'idle' as const,
        },
        pointerTarget: null,
      };
    }
    const activeTarget =
      resolveImmediateRouteWaypoint(route, hero.position, hero.radius + NAVIGATION_ARRIVAL_MARGIN) ??
      input.pointerTarget;
    const delta = {
      x: activeTarget.x - hero.position.x,
      y: activeTarget.y - hero.position.y,
    };
    const distance = Math.hypot(delta.x, delta.y);

    if (distance <= hero.radius) {
      return {
        map,
        hero: {
          ...hero,
          velocity: { x: 0, y: 0 },
          movementMode: 'idle' as const,
          animationState: 'idle' as const,
        },
        pointerTarget: null,
      };
    }

    const direction = normalizeInputVector(delta);
    const velocity = { x: direction.x * effectiveSpeed, y: direction.y * effectiveSpeed };
    const movement = stepActorInternal(map, hero, velocity, dtSeconds, doors, movementOptions);
    const nextPosition = movement.position;

    if (nextPosition.x === hero.position.x && nextPosition.y === hero.position.y) {
      return {
        map: movement.map,
        hero: {
          ...hero,
          velocity: { x: 0, y: 0 },
          movementMode: 'idle' as const,
          animationState: 'idle' as const,
        },
        pointerTarget: route.waypoints.length > 0 ? input.pointerTarget : null,
      };
    }
    const nextHero = {
      ...hero,
      position: nextPosition,
      velocity,
      facing: facingFromVelocity(velocity, hero.facing),
      zoneId: resolveZoneId(movement.map, nextPosition),
      movementMode: 'auto' as const,
      animationState: 'walk' as const,
      carriedObjectWeightKg: carriedWeightKg,
    };
    const carryResult = syncCarriedObjectAfterHeroMove(movement.map, hero, nextHero, doors);

    return {
      map: carryResult.map,
      hero: carryResult.hero,
      pointerTarget: input.pointerTarget,
    };
  }

  return {
    map,
    hero: {
      ...hero,
      velocity: { x: 0, y: 0 },
      movementMode: 'idle' as const,
      animationState: 'idle' as const,
      carriedObjectId: carriedObject ? hero.carriedObjectId : null,
      carriedObjectWeightKg: carriedObject ? carriedWeightKg : 0,
    },
    pointerTarget: null,
  };
}

export function toggleMysteryRoomHeroCarryObject(runtime: MysteryRoomLocalRuntime): MysteryRoomLocalRuntime {
  if (runtime.hero.carriedObjectId) {
    const carriedObject = getCarriedObject(runtime.map, runtime.hero.carriedObjectId);
    return {
      ...runtime,
      hero: {
        ...runtime.hero,
        carriedObjectId: null,
        carriedObjectWeightKg: 0,
      },
      events: [`Предмет отпущен: ${carriedObject?.label ?? runtime.hero.carriedObjectId}`, ...runtime.events].slice(0, 10),
    };
  }

  const candidate = findCarryableCustomObjectForHero(runtime.map, runtime.hero);
  if (!candidate) {
    return {
      ...runtime,
      events: ['Нет подходящего предмета для переноски', ...runtime.events].slice(0, 10),
    };
  }

  const weightKg = customObjectWeightKg(candidate.object);
  const placedObject = placeCustomObjectInFrontOfHero(candidate.object, runtime.hero);
  if (!canPlacePushedObject(runtime.map, placedObject, candidate.object.id, runtime.doors)) {
    return {
      ...runtime,
      events: [`Нельзя взять предмет: ${candidate.object.label}`, ...runtime.events].slice(0, 10),
    };
  }

  return {
    ...runtime,
    map: replaceCustomObject(runtime.map, candidate.index, placedObject),
    hero: {
      ...runtime.hero,
      hidden: false,
      hideZoneId: null,
      carriedObjectId: candidate.object.id,
      carriedObjectWeightKg: weightKg,
    },
    input: {
      ...runtime.input,
      pointerTarget: null,
    },
    events: [`Предмет взят: ${candidate.object.label} (${Math.round(weightKg)} кг)`, ...runtime.events].slice(0, 10),
  };
}

export function carriedObjectSpeedMultiplier(weightKg: number, strengthKg: number): number {
  if (!Number.isFinite(weightKg) || weightKg <= 0) {
    return 1;
  }
  const safeStrengthKg = Math.max(1, Number.isFinite(strengthKg) ? strengthKg : DEFAULT_HERO_STRENGTH_KG);
  const loadRatio = Math.min(1, Math.max(0, weightKg / safeStrengthKg));
  const multiplier = 1 - loadRatio * HERO_CARRY_MAX_SLOWDOWN;
  return Math.max(HERO_CARRY_MIN_SPEED_MULTIPLIER, Math.round(multiplier * 100) / 100);
}

export function stepActor(
  map: MysteryRoomMapDefinition,
  actor: MovableActor,
  velocity: MysteryRoomVec2,
  dtSeconds: number,
  doors: MysteryRoomDoorRuntimeState[],
) {
  return stepActorInternal(map, actor, velocity, dtSeconds, doors).position;
}

function stepActorInternal(
  map: MysteryRoomMapDefinition,
  actor: MovableActor,
  velocity: MysteryRoomVec2,
  dtSeconds: number,
  doors: MysteryRoomDoorRuntimeState[],
  options: StepActorOptions = {},
): StepActorResult {
  let nextMap = map;
  let nextPosition = { ...actor.position };
  const desiredX = nextPosition.x + velocity.x * dtSeconds;
  const desiredY = nextPosition.y + velocity.y * dtSeconds;

  const xMove = attemptActorAxisMove(nextMap, actor, nextPosition, { x: desiredX, y: nextPosition.y }, doors, options);
  nextMap = xMove.map;
  nextPosition = xMove.position;

  const yMove = attemptActorAxisMove(nextMap, actor, nextPosition, { x: nextPosition.x, y: desiredY }, doors, options);

  return yMove;
}

function attemptActorAxisMove(
  map: MysteryRoomMapDefinition,
  actor: MovableActor,
  from: MysteryRoomVec2,
  target: MysteryRoomVec2,
  doors: MysteryRoomDoorRuntimeState[],
  options: StepActorOptions,
): StepActorResult {
  if (from.x === target.x && from.y === target.y) {
    return { map, position: from };
  }

  if (movementCrossesWall(map.walls, from, target, actor.radius)) {
    return { map, position: from };
  }

  if (canTraversePoint(map, target, doors, actor.radius, { ignoreCustomObjectId: options.ignoreCustomObjectId })) {
    return { map, position: target };
  }

  if (!options.pushStrengthKg || options.pushStrengthKg <= 0) {
    return { map, position: from };
  }

  return tryPushBlockingObject(map, actor, from, target, doors, options.pushStrengthKg, options.ignoreCustomObjectId);
}

function tryPushBlockingObject(
  map: MysteryRoomMapDefinition,
  actor: MovableActor,
  from: MysteryRoomVec2,
  target: MysteryRoomVec2,
  doors: MysteryRoomDoorRuntimeState[],
  pushStrengthKg: number,
  ignoreCustomObjectId?: string,
): StepActorResult {
  const delta = {
    x: target.x - from.x,
    y: target.y - from.y,
  };
  if (Math.abs(delta.x) < 0.0001 && Math.abs(delta.y) < 0.0001) {
    return { map, position: from };
  }

  const samples = actorTraversalSamples(target, actor.radius);
  if (samples.some((sample) => !pointInsideWalkableArea(map, sample) || pointHitsWorldMovementBlocker(map, sample, doors))) {
    return { map, position: from };
  }

  const blocker = samples
    .map((sample) => findCustomObjectMovementBlocker(map, sample, { ignoreCustomObjectId }))
    .find((entry): entry is CustomObjectBlocker => !!entry);

  if (!blocker || !isPushableCustomObject(blocker.object, pushStrengthKg)) {
    return { map, position: from };
  }

  const pushedObject = offsetCustomObject(blocker.object, delta);
  if (!canPlacePushedObject(map, pushedObject, blocker.object.id, doors)) {
    return { map, position: from };
  }

  const pushedMap = {
    ...map,
    customObjects: map.customObjects.map((object, index) => (index === blocker.index ? pushedObject : object)),
  };

  if (!canTraversePoint(pushedMap, target, doors, actor.radius, { ignoreCustomObjectId: blocker.object.id })) {
    return { map, position: from };
  }

  return { map: pushedMap, position: target };
}

function movementCrossesWall(
  walls: MysteryRoomMapDefinition['walls'],
  from: MysteryRoomVec2,
  to: MysteryRoomVec2,
  radius: number,
) {
  if (from.x === to.x && from.y === to.y) {
    return false;
  }

  for (const offset of sampleOffsets(radius)) {
    const sampleStart = {
      x: from.x + offset.x,
      y: from.y + offset.y,
    };
    const sampleEnd = {
      x: to.x + offset.x,
      y: to.y + offset.y,
    };

    if (walls.some((wall) => segmentsIntersect(sampleStart, sampleEnd, wall.start, wall.end))) {
      return true;
    }
  }

  return false;
}

function sampleOffsets(radius: number) {
  return [
    { x: 0, y: 0 },
    { x: -radius, y: 0 },
    { x: radius, y: 0 },
    { x: 0, y: -radius },
    { x: 0, y: radius },
  ];
}

function actorTraversalSamples(point: MysteryRoomVec2, radius: number) {
  return sampleOffsets(radius).map((offset) => ({
    x: point.x + offset.x,
    y: point.y + offset.y,
  }));
}

function segmentsIntersect(
  aStart: MysteryRoomVec2,
  aEnd: MysteryRoomVec2,
  bStart: MysteryRoomVec2,
  bEnd: MysteryRoomVec2,
) {
  const d1 = direction(bStart, bEnd, aStart);
  const d2 = direction(bStart, bEnd, aEnd);
  const d3 = direction(aStart, aEnd, bStart);
  const d4 = direction(aStart, aEnd, bEnd);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  return (
    (Math.abs(d1) <= 0.0001 && pointOnSegment(bStart, bEnd, aStart)) ||
    (Math.abs(d2) <= 0.0001 && pointOnSegment(bStart, bEnd, aEnd)) ||
    (Math.abs(d3) <= 0.0001 && pointOnSegment(aStart, aEnd, bStart)) ||
    (Math.abs(d4) <= 0.0001 && pointOnSegment(aStart, aEnd, bEnd))
  );
}

function direction(a: MysteryRoomVec2, b: MysteryRoomVec2, c: MysteryRoomVec2) {
  return (c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x);
}

function pointOnSegment(a: MysteryRoomVec2, b: MysteryRoomVec2, p: MysteryRoomVec2) {
  return (
    p.x >= Math.min(a.x, b.x) - 0.0001 &&
    p.x <= Math.max(a.x, b.x) + 0.0001 &&
    p.y >= Math.min(a.y, b.y) - 0.0001 &&
    p.y <= Math.max(a.y, b.y) + 0.0001
  );
}

function pointInsideWalkableArea(map: MysteryRoomMapDefinition, point: MysteryRoomVec2) {
  return map.rooms.some((room) => room.floor.some((floor) => rectContainsPoint(floor, point)));
}

function pointHitsMovementBlocker(
  map: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
  doors: MysteryRoomDoorRuntimeState[],
  options: TraverseOptions = {},
) {
  return pointHitsWorldMovementBlocker(map, point, doors) || !!findCustomObjectMovementBlocker(map, point, options);
}

function pointHitsWorldMovementBlocker(
  map: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
  doors: MysteryRoomDoorRuntimeState[],
) {
  if (map.occluders.some((occluder) => occluder.blocksMovement && rectContainsPoint(occluder.rect, point))) {
    return true;
  }

  return doors.some((door) => !isDoorTraversable(door) && rectContainsPoint(door.panel, point));
}

function findCustomObjectMovementBlocker(
  map: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
  options: TraverseOptions = {},
): CustomObjectBlocker | null {
  for (let index = 0; index < (map.customObjects ?? []).length; index += 1) {
    const object = map.customObjects[index];
    if (!object || object.id === options.ignoreCustomObjectId || !isMovementBlockingCustomObject(object)) {
      continue;
    }
    if (pointHitsCollisionShape(object.collision, point)) {
      if (object.assetAlphaCollisionEnabled && isMysteryRoomCustomObjectAlphaOpaqueAtPoint(object, point) === false) {
        continue;
      }
      return { object, index };
    }
  }

  return null;
}

function isMovementBlockingCustomObject(object: MysteryRoomCustomObjectDefinition) {
  if (object.collision.kind !== 'rect' && object.collision.kind !== 'circle' && object.collision.kind !== 'polygon') {
    return false;
  }
  return (object.collisionEnabled ?? object.collision.blocksMovement) && object.collision.blocksMovement;
}

function pointHitsCollisionShape(collision: MysteryRoomCollisionShape, point: MysteryRoomVec2) {
  if (collision.kind === 'rect') {
    return rectContainsPoint(collision.rect, point);
  }
  if (collision.kind === 'circle') {
    return Math.hypot(point.x - collision.center.x, point.y - collision.center.y) <= collision.radius;
  }
  if (collision.kind === 'polygon') {
    return pointInPolygon(point, collision.points);
  }
  return false;
}

function isPushableCustomObject(object: MysteryRoomCustomObjectDefinition, pushStrengthKg: number) {
  return isMovementBlockingCustomObject(object) && object.physicsEnabled === true && customObjectWeightKg(object) <= pushStrengthKg;
}

function isCarryableCustomObject(object: MysteryRoomCustomObjectDefinition, strengthKg: number) {
  return isPushableCustomObject(object, strengthKg);
}

function customObjectWeightKg(object: MysteryRoomCustomObjectDefinition) {
  return typeof object.weightKg === 'number' && Number.isFinite(object.weightKg)
    ? Math.max(0, object.weightKg)
    : DEFAULT_PUSHABLE_OBJECT_WEIGHT_KG;
}

function offsetCustomObject(
  object: MysteryRoomCustomObjectDefinition,
  delta: MysteryRoomVec2,
): MysteryRoomCustomObjectDefinition {
  return {
    ...object,
    rect: offsetRect(object.rect, delta),
    collision: offsetCollision(object.collision, delta),
  };
}

function offsetCollision(collision: MysteryRoomCollisionShape, delta: MysteryRoomVec2): MysteryRoomCollisionShape {
  if (collision.kind === 'rect') {
    return { ...collision, rect: offsetRect(collision.rect, delta) };
  }
  if (collision.kind === 'circle') {
    return {
      ...collision,
      center: {
        x: collision.center.x + delta.x,
        y: collision.center.y + delta.y,
      },
    };
  }
  if (collision.kind === 'polygon') {
    return {
      ...collision,
      points: collision.points.map((point) => ({ x: point.x + delta.x, y: point.y + delta.y })),
    };
  }
  return collision;
}

function offsetRect(rect: MysteryRoomRect, delta: MysteryRoomVec2): MysteryRoomRect {
  return {
    ...rect,
    x: rect.x + delta.x,
    y: rect.y + delta.y,
  };
}

function getCarriedObject(map: MysteryRoomMapDefinition, objectId: string | null | undefined) {
  if (!objectId) {
    return null;
  }
  return map.customObjects.find((object) => object.id === objectId) ?? null;
}

function syncCarriedObjectAfterHeroMove(
  map: MysteryRoomMapDefinition,
  previousHero: MysteryRoomHeroState,
  nextHero: MysteryRoomHeroState,
  doors: MysteryRoomDoorRuntimeState[],
): { map: MysteryRoomMapDefinition; hero: MysteryRoomHeroState } {
  const object = getCarriedObject(map, nextHero.carriedObjectId);
  if (!object) {
    return {
      map,
      hero: {
        ...nextHero,
        carriedObjectId: null,
        carriedObjectWeightKg: 0,
      },
    };
  }

  const placedObject = placeCustomObjectInFrontOfHero(object, nextHero);
  if (!canPlacePushedObject(map, placedObject, object.id, doors)) {
    return {
      map,
      hero: {
        ...previousHero,
        velocity: { x: 0, y: 0 },
        movementMode: 'idle',
        animationState: 'idle',
        carriedObjectWeightKg: customObjectWeightKg(object),
      },
    };
  }

  return {
    map: replaceCustomObject(map, map.customObjects.findIndex((candidate) => candidate.id === object.id), placedObject),
    hero: {
      ...nextHero,
      carriedObjectWeightKg: customObjectWeightKg(object),
    },
  };
}

function findCarryableCustomObjectForHero(
  map: MysteryRoomMapDefinition,
  hero: MysteryRoomHeroState,
): CustomObjectBlocker | null {
  const facing = facingVector(hero.facing);
  const strengthKg = hero.strengthKg ?? DEFAULT_HERO_STRENGTH_KG;
  let best: (CustomObjectBlocker & { score: number }) | null = null;

  for (let index = 0; index < map.customObjects.length; index += 1) {
    const object = map.customObjects[index];
    if (!isCarryableCustomObject(object, strengthKg)) {
      continue;
    }
    const footprint = customObjectFootprint(object);
    if (!footprint) {
      continue;
    }

    const delta = {
      x: footprint.center.x - hero.position.x,
      y: footprint.center.y - hero.position.y,
    };
    const distance = Math.hypot(delta.x, delta.y);
    if (distance <= 0.0001 || distance > hero.radius + footprint.radius + HERO_CARRY_REACH) {
      continue;
    }

    const frontDot = (delta.x * facing.x + delta.y * facing.y) / distance;
    if (frontDot < -0.2) {
      continue;
    }
    const score = distance + (frontDot >= 0.25 ? 0 : 120);
    if (!best || score < best.score) {
      best = { object, index, score };
    }
  }

  return best;
}

function placeCustomObjectInFrontOfHero(
  object: MysteryRoomCustomObjectDefinition,
  hero: MysteryRoomHeroState,
): MysteryRoomCustomObjectDefinition {
  const footprint = customObjectFootprint(object);
  if (!footprint) {
    return object;
  }
  const facing = facingVector(hero.facing);
  const attachDistance = hero.radius + footprint.radius + HERO_CARRY_GAP;
  const targetCenter = {
    x: hero.position.x + facing.x * attachDistance,
    y: hero.position.y + facing.y * attachDistance,
  };
  return offsetCustomObject(object, {
    x: targetCenter.x - footprint.center.x,
    y: targetCenter.y - footprint.center.y,
  });
}

function customObjectFootprint(object: MysteryRoomCustomObjectDefinition): { center: MysteryRoomVec2; radius: number } | null {
  const bounds = collisionBounds(object.collision) ?? object.rect;
  if (!bounds) {
    return null;
  }
  return {
    center: {
      x: bounds.x + bounds.width / 2,
      y: bounds.y + bounds.height / 2,
    },
    radius: Math.max(bounds.width, bounds.height) / 2,
  };
}

function facingVector(facing: MysteryRoomFacing): MysteryRoomVec2 {
  switch (facing) {
    case 'up':
      return { x: 0, y: -1 };
    case 'left':
      return { x: -1, y: 0 };
    case 'right':
      return { x: 1, y: 0 };
    case 'down':
    default:
      return { x: 0, y: 1 };
  }
}

function replaceCustomObject(
  map: MysteryRoomMapDefinition,
  objectIndex: number,
  object: MysteryRoomCustomObjectDefinition,
): MysteryRoomMapDefinition {
  if (objectIndex < 0) {
    return map;
  }
  return {
    ...map,
    customObjects: map.customObjects.map((candidate, index) => (index === objectIndex ? object : candidate)),
  };
}

function canPlacePushedObject(
  map: MysteryRoomMapDefinition,
  object: MysteryRoomCustomObjectDefinition,
  ignoreObjectId: string,
  doors: MysteryRoomDoorRuntimeState[],
) {
  if (!isMovementBlockingCustomObject(object)) {
    return true;
  }

  const bounds = collisionBounds(object.collision);
  if (!bounds) {
    return true;
  }

  if (rectSamples(bounds).some((point) => !pointInsideWalkableArea(map, point))) {
    return false;
  }
  if (map.occluders.some((occluder) => occluder.blocksMovement && rectsOverlap(bounds, occluder.rect))) {
    return false;
  }
  if (doors.some((door) => !isDoorTraversable(door) && rectsOverlap(bounds, door.panel))) {
    return false;
  }
  if (map.walls.some((wall) => segmentIntersectsRect(wall.start, wall.end, bounds))) {
    return false;
  }
  if (
    map.customObjects.some((candidate) => {
      if (candidate.id === ignoreObjectId || !isMovementBlockingCustomObject(candidate)) {
        return false;
      }
      return collisionShapesOverlap(object.collision, candidate.collision);
    })
  ) {
    return false;
  }

  return true;
}

function collisionBounds(collision: MysteryRoomCollisionShape): MysteryRoomRect | null {
  if (collision.kind === 'rect') {
    return collision.rect;
  }
  if (collision.kind === 'circle') {
    return {
      x: collision.center.x - collision.radius,
      y: collision.center.y - collision.radius,
      width: collision.radius * 2,
      height: collision.radius * 2,
    };
  }
  if (collision.kind === 'polygon') {
    if (collision.points.length === 0) {
      return null;
    }
    const minX = Math.min(...collision.points.map((point) => point.x));
    const maxX = Math.max(...collision.points.map((point) => point.x));
    const minY = Math.min(...collision.points.map((point) => point.y));
    const maxY = Math.max(...collision.points.map((point) => point.y));
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  return null;
}

function pointInPolygon(point: MysteryRoomVec2, polygon: MysteryRoomVec2[]) {
  if (polygon.length < 3) {
    return false;
  }
  let inside = false;
  for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index, index += 1) {
    const current = polygon[index];
    const previous = polygon[previousIndex];
    const denominator = previous.y - current.y;
    const crosses =
      current.y > point.y !== previous.y > point.y &&
      Math.abs(denominator) > 0.000001 &&
      point.x < ((previous.x - current.x) * (point.y - current.y)) / denominator + current.x;
    if (crosses) {
      inside = !inside;
    }
  }
  return inside;
}

function rectSamples(rect: MysteryRoomRect): MysteryRoomVec2[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 },
  ];
}

function collisionShapesOverlap(a: MysteryRoomCollisionShape, b: MysteryRoomCollisionShape) {
  const aBounds = collisionBounds(a);
  const bBounds = collisionBounds(b);
  return !!aBounds && !!bBounds && rectsOverlap(aBounds, bBounds);
}

function rectsOverlap(a: MysteryRoomRect, b: MysteryRoomRect) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function segmentIntersectsRect(start: MysteryRoomVec2, end: MysteryRoomVec2, rect: MysteryRoomRect) {
  if (rectContainsPoint(rect, start) || rectContainsPoint(rect, end)) {
    return true;
  }
  const topLeft = { x: rect.x, y: rect.y };
  const topRight = { x: rect.x + rect.width, y: rect.y };
  const bottomLeft = { x: rect.x, y: rect.y + rect.height };
  const bottomRight = { x: rect.x + rect.width, y: rect.y + rect.height };
  return (
    segmentsIntersect(start, end, topLeft, topRight) ||
    segmentsIntersect(start, end, topRight, bottomRight) ||
    segmentsIntersect(start, end, bottomRight, bottomLeft) ||
    segmentsIntersect(start, end, bottomLeft, topLeft)
  );
}

export function isDoorTraversable(door: MysteryRoomDoorRuntimeState) {
  if (door.state === 'open') {
    return true;
  }

  return door.openProgress >= 0.72 && (door.state === 'opening' || door.state === 'closing');
}
